import React, { useMemo, useState } from 'react';
import { useQuery } from 'react-admin';
import { useForm, useFormState } from 'react-final-form';
import moment from 'moment';

import Box from '@material-ui/core/Box';
import Chip from '@material-ui/core/Chip';
import CircularProgress from '@material-ui/core/CircularProgress';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import ListItemText from '@material-ui/core/ListItemText';
import MenuItem from '@material-ui/core/MenuItem';
import Popover from '@material-ui/core/Popover';
import TextField from '@material-ui/core/TextField';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';

import { SOFT_GOODS_BY_FIELD, formatLotText, totalQty } from './softGoods';
import { useSoftGoods, linesForField, setFieldLines } from './softGoodsStore';

/**
 * Lot picker for one soft good on the Phlebotomy tab.
 *
 * Replaces a free-text lot number box. The phlebotomist picks from the lots
 * that inventory has actually released, one or more per donation, with a
 * quantity each - two bottles from two different lots is the normal case, not
 * an edge case.
 *
 * What it writes into the form:
 *   lotnumber_<x>   human readable text, unchanged in shape from before, so
 *                   the worksheet and every existing report keep working
 *   <qty field>     the total units, kept in step automatically
 *
 * The structured selection the API reconciles inventory against lives in
 * softGoodsStore, not the form, so that seeding it on open does not mark an
 * untouched record dirty.
 *
 * Availability shown is `qty_release` plus whatever this donation already
 * holds on that lot, because a saved donation has already drawn its units
 * down - without adding them back, editing a record would look as though the
 * stock had vanished.
 */

const useStyles = makeStyles(theme => ({
    root: {
        width: '100%',
    },
    field: {
        width: '100%',
        cursor: 'pointer',
        '& input': { cursor: 'pointer' },
    },
    chips: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 4,
        marginTop: 4,
    },
    chip: {
        maxWidth: '100%',
    },
    chipWarn: {
        backgroundColor: '#fff6e0',
        border: '1px solid #e0a800',
    },
    chipExpired: {
        backgroundColor: '#ffe8e8',
        border: '1px solid #d32f2f',
    },
    popover: {
        padding: theme.spacing(1),
        width: 380,
        maxHeight: 420,
    },
    search: {
        marginBottom: theme.spacing(1),
    },
    list: {
        maxHeight: 260,
        overflowY: 'auto',
    },
    row: {
        alignItems: 'flex-start',
        paddingTop: 4,
        paddingBottom: 4,
    },
    rowWarn: { backgroundColor: '#fff6e0' },
    rowSelected: { backgroundColor: '#e8f0fe' },
    meta: {
        fontSize: 11,
        color: '#666',
    },
    metaWarn: {
        fontSize: 11,
        color: '#b26a00',
        fontWeight: 600,
    },
    qtyBox: {
        width: 64,
        marginLeft: 8,
    },
    empty: {
        padding: theme.spacing(1),
        fontSize: 12,
        color: '#888',
    },
    hint: {
        fontSize: 11,
        color: '#888',
        marginTop: 2,
    },
    error: {
        fontSize: 11,
        color: '#d32f2f',
        marginTop: 2,
    },
}));

const fmtDate = value => (value ? moment(value).format('MM/DD/YYYY') : '-');

const SoftGoodLotInput = ({ field, disabled }) => {
    const classes = useStyles();
    const spec = SOFT_GOODS_BY_FIELD[field];

    const form = useForm();
    const { values } = useFormState({ subscription: { values: true } });

    const [anchorEl, setAnchorEl] = useState(null);
    const [search, setSearch] = useState('');

    // Every released lot whose supply type is mapped to this field. Sorted by
    // expiry so the soonest-to-expire lot is the one offered first.
    const { data: lots, loading } = useQuery({
        type: 'getList',
        resource: 'supplylots',
        payload: {
            filter: { phlebo_field: field, has_release: 1, not_expired: 1 },
            sort: { field: 'expiry_date', order: 'ASC' },
            pagination: { page: 1, perPage: 200 },
        },
    });

    const softGoods = useSoftGoods();

    /** Just this field's slice of the shared selection. */
    const mine = useMemo(
        () => linesForField(softGoods, field),
        [softGoods, field]
    );

    const pickedQty = useMemo(() => {
        const map = {};
        mine.forEach(line => { map[line.receipt_id] = Number(line.qty) || 0; });
        return map;
    }, [mine]);

    /**
     * Units of a lot this donation had already committed before this edit.
     * Seeded by SoftGoodUsageLoader from the ledger; without it the picker
     * would refuse to let you keep a lot whose whole release balance is the
     * stock this very donation is holding.
     */
    const committed = softGoods.committed || {};

    const available = receiptId => {
        const lot = (lots || []).find(l => String(l.id) === String(receiptId));
        const onHand = lot ? (Number(lot.qty_release) || 0) : 0;
        return onHand + (Number(committed[receiptId]) || 0);
    };

    /** Lots to offer: everything released, plus anything already picked that
     *  has since dropped off the released list, so a saved record never loses
     *  the lot it was recorded against. */
    const options = useMemo(() => {
        const list = (lots || []).slice();
        const seen = {};
        list.forEach(lot => { seen[String(lot.id)] = true; });
        mine.forEach(line => {
            if (!seen[String(line.receipt_id)]) {
                list.push({
                    id: line.receipt_id,
                    lot_no: line.lot_no,
                    expiry_date: line.expiry_date,
                    qty_release: 0,
                    supply_type: line.supply_type,
                    stale: true,
                });
            }
        });
        return list;
    }, [lots, mine]);

    const visible = useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term) { return options; }
        return options.filter(lot =>
            String(lot.lot_no || '').toLowerCase().indexOf(term) > -1
            || String(lot.supply_type || '').toLowerCase().indexOf(term) > -1
        );
    }, [options, search]);

    /** Push this field's lines into the store and refresh the two form fields
     *  that the rest of the app already reads. */
    const commit = (lines) => {
        setFieldLines(field, lines);
        form.change(spec.lotSource, formatLotText(lines));

        const total = totalQty(lines);
        // `ckit` is a SelectInput over string choices; the others are text.
        form.change(spec.qtySource, total ? String(total) : '');
    };

    const setQty = (lot, qty) => {
        const max = available(lot.id);
        const next = Math.max(0, Math.min(Number(qty) || 0, max));

        const without = mine.filter(line => String(line.receipt_id) !== String(lot.id));
        if (next <= 0) {
            commit(without);
            return;
        }
        commit(without.concat([{
            phlebo_field: field,
            receipt_id: lot.id,
            lot_no: lot.lot_no,
            supply_type: lot.supply_type,
            expiry_date: lot.expiry_date,
            qty: next,
        }]));
    };

    const toggle = (lot) => {
        if (pickedQty[lot.id]) {
            setQty(lot, 0);
        } else {
            setQty(lot, 1);
        }
    };

    /** A scanned barcode arrives as text ending in Enter - if it matches one
     *  lot exactly, add it and clear the box so the next scan can follow. */
    const onSearchKey = (event) => {
        if (event.key !== 'Enter') { return; }
        event.preventDefault();
        const term = search.trim().toLowerCase();
        if (!term) { return; }
        const exact = options.filter(lot => String(lot.lot_no || '').toLowerCase() === term);
        const match = exact.length === 1 ? exact[0] : (visible.length === 1 ? visible[0] : null);
        if (match) {
            setQty(match, (pickedQty[match.id] || 0) + 1);
            setSearch('');
        }
    };

    // Records saved before the picker existed hold free text in lotnumber_*.
    // Show it until the phlebotomist picks a lot, rather than blanking a
    // historical value the moment the screen opens.
    const text = mine.length ? formatLotText(mine) : (values[spec.lotSource] || '');
    const overCommitted = mine.filter(line => line.qty > available(line.receipt_id));

    const warnDays = lot => (lot && lot.expiry_warn_days) || 30;
    const expiringSoon = lot =>
        lot && lot.days_to_expire !== undefined && lot.days_to_expire !== null
        && lot.days_to_expire >= 0 && lot.days_to_expire <= warnDays(lot);
    const expired = lot =>
        lot && lot.days_to_expire !== undefined && lot.days_to_expire !== null
        && lot.days_to_expire < 0;

    return (
        <div className={classes.root}>
            <TextField
                className={classes.field}
                value={text}
                placeholder={disabled ? '' : 'Select lot(s)'}
                onClick={disabled ? undefined : e => setAnchorEl(e.currentTarget)}
                disabled={disabled}
                InputProps={{
                    readOnly: true,
                    endAdornment: (
                        <InputAdornment position="end">
                            {loading
                                ? <CircularProgress size={14} />
                                : (
                                    <IconButton
                                        size="small"
                                        disabled={disabled}
                                        onClick={e => {
                                            e.stopPropagation();
                                            setAnchorEl(e.currentTarget);
                                        }}
                                        aria-label={'Select ' + spec.label + ' lots'}
                                    >
                                        <span style={{ fontSize: 12 }}>▾</span>
                                    </IconButton>
                                )}
                        </InputAdornment>
                    ),
                }}
            />

            {mine.length > 0 && (
                <div className={classes.chips}>
                    {mine.map(line => {
                        const lot = options.find(o => String(o.id) === String(line.receipt_id));
                        let chipClass = classes.chip;
                        if (expired(lot)) { chipClass += ' ' + classes.chipExpired; }
                        else if (expiringSoon(lot)) { chipClass += ' ' + classes.chipWarn; }
                        return (
                            <Tooltip
                                key={line.receipt_id}
                                title={(line.supply_type || spec.label)
                                    + ' · exp ' + fmtDate(line.expiry_date)}
                            >
                                <Chip
                                    size="small"
                                    className={chipClass}
                                    label={line.lot_no + ' × ' + line.qty}
                                    onDelete={disabled ? undefined : () => setQty(
                                        { id: line.receipt_id, lot_no: line.lot_no }, 0
                                    )}
                                />
                            </Tooltip>
                        );
                    })}
                </div>
            )}

            {overCommitted.length > 0 && (
                <div className={classes.error}>
                    Not enough released stock for {overCommitted.map(l => l.lot_no).join(', ')}
                </div>
            )}

            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={() => { setAnchorEl(null); setSearch(''); }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
                <div className={classes.popover}>
                    <Typography variant="subtitle2">{spec.label} — released lots</Typography>

                    <TextField
                        className={classes.search}
                        fullWidth
                        margin="dense"
                        placeholder="Search or scan lot #"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        onKeyDown={onSearchKey}
                        autoFocus
                    />

                    <div className={classes.list}>
                        {loading && <div className={classes.empty}>Loading lots…</div>}

                        {!loading && visible.length === 0 && (
                            <div className={classes.empty}>
                                No released lots are mapped to {spec.label}. Check the supply type's
                                Phlebotomy Field in Inventory setup, and that a lot has been QA
                                approved and released.
                            </div>
                        )}

                        {visible.map(lot => {
                            const picked = pickedQty[lot.id] || 0;
                            const max = available(lot.id);
                            let rowClass = classes.row;
                            if (picked) { rowClass += ' ' + classes.rowSelected; }
                            else if (expiringSoon(lot)) { rowClass += ' ' + classes.rowWarn; }

                            return (
                                <MenuItem
                                    key={lot.id}
                                    className={rowClass}
                                    onClick={() => toggle(lot)}
                                    disabled={disabled || (max <= 0 && !picked)}
                                >
                                    <ListItemText
                                        primary={lot.lot_no}
                                        secondary={
                                            <span
                                                className={expiringSoon(lot) || expired(lot)
                                                    ? classes.metaWarn : classes.meta}
                                            >
                                                {lot.supply_type ? lot.supply_type + ' · ' : ''}
                                                exp {fmtDate(lot.expiry_date)}
                                                {' · '}{max} available
                                                {expired(lot) ? ' · EXPIRED' : ''}
                                                {!expired(lot) && expiringSoon(lot) ? ' · expiring' : ''}
                                                {lot.stale ? ' · no longer released' : ''}
                                            </span>
                                        }
                                    />
                                    <Box onClick={e => e.stopPropagation()}>
                                        <TextField
                                            className={classes.qtyBox}
                                            type="number"
                                            size="small"
                                            label="Qty"
                                            value={picked || ''}
                                            disabled={disabled}
                                            inputProps={{ min: 0, max: max }}
                                            InputLabelProps={{ shrink: true }}
                                            onChange={e => setQty(lot, e.target.value)}
                                        />
                                    </Box>
                                </MenuItem>
                            );
                        })}
                    </div>

                    <div className={classes.hint}>
                        Pick every lot used. Stock comes off on save.
                    </div>
                </div>
            </Popover>
        </div>
    );
};

export default SoftGoodLotInput;
